// src/services/api/SocialApi.tsx
import { BaseAPI } from "./BaseApi";
import type { IFollow, FollowPage, FeedPage, CreatedPost, LikersResponse } from "../../types/social";
import type { FeedItem } from "../../types/Post/post";
import type { Comment, CommentsResponse } from "../../types/comment";

export class SocialAPI extends BaseAPI {
  constructor() {
    super(""); // uses same base URL as other API classes
  }

  // 👥 Follow/Unfollow endpoints
  async follow(targetUserId: string) {
    return this.request<{ follow?: IFollow; message?: string; followRequestCreated?: boolean; status?: string }>("/api/social/follow", {
      method: "POST",
      body: JSON.stringify({ targetUserId }),
      auth: true,
    });
  }

  async unfollow(targetUserId: string) {
    return this.request<{ message: string }>(`/api/social/follow/${targetUserId}`, {
      method: "DELETE",
      auth: true,
    });
  }

  async isFollowing(targetUserId: string) {
    return this.request<{ isFollowing: boolean }>(`/api/social/follow/status/${targetUserId}`, {
      method: "GET",
      auth: true,
    });
  }

  // 🔐 Follow request endpoints (private accounts)
  async getFollowRequestStatus(targetUserId: string) {
    return this.request<{ hasPendingRequest: boolean }>(
      `/api/social/follow-requests/status/${targetUserId}`,
      { method: "GET", auth: true }
    );
  }

  async cancelFollowRequest(targetUserId: string) {
    return this.request<{ message: string }>(
      `/api/social/follow-requests/${targetUserId}`,
      { method: "DELETE", auth: true }
    );
  }

  async listFollowRequests(page: number = 1) {
    const limit = 20;
    return this.request<{ requests: any[]; total: number }>(
      `/api/social/follow-requests?limit=${limit}&page=${page}`,
      { method: "GET", auth: true }
    );
  }

  async approveFollowRequest(requestId: string) {
    return this.request<{ message: string }>(
      `/api/social/follow-requests/${requestId}/approve`,
      { method: "POST", auth: true }
    );
  }

  async rejectFollowRequest(requestId: string) {
    return this.request<{ message: string }>(
      `/api/social/follow-requests/${requestId}/reject`,
      { method: "POST", auth: true }
    );
  }

  // 📋 Follower/Following list endpoints
  async getFollowers(userId: string, page: number = 1) {
    const limit = 20;
    return this.request<FollowPage>(`/api/social/followers/${userId}?limit=${limit}&page=${page}`, {
      method: "GET",
      auth: true,
    });
  }

  async getFollowing(userId: string, page: number = 1) {
    const limit = 20;
    return this.request<FollowPage>(`/api/social/following/${userId}?limit=${limit}&page=${page}`, {
      method: "GET",
      auth: true,
    });
  }

  // 📰 Feed endpoint
  async getFeed(cursor?: string, limit: number = 20) {
    const params = new URLSearchParams();
    if (cursor) params.append("cursor", cursor);
    params.append("limit", limit.toString());

    const queryString = params.toString();
    return this.request<FeedPage>(`/api/social/feed${queryString ? `?${queryString}` : ""}`, {
      method: "GET",
      auth: true,
    });
  }

  async getNewFeedPosts(since: string, limit: number = 20): Promise<{ posts: FeedItem[]; nextCursor: string | null }> {
    const params = new URLSearchParams({ since, limit: limit.toString() });
    return this.request<{ posts: FeedItem[]; nextCursor: string | null }>(
      `/api/social/feed/new?${params}`,
      { method: "GET", auth: true }
    );
  }

  // 📄 User posts endpoint
  async getUserPosts(userId: string, cursor?: string, limit: number = 20): Promise<FeedPage> {
    const params = new URLSearchParams();
    if (cursor) params.append("cursor", cursor);
    params.append("limit", limit.toString());
    const qs = params.toString();
    return this.request<FeedPage>(
      `/api/social/users/${userId}/posts${qs ? `?${qs}` : ""}`,
      { method: "GET", auth: true }
    );
  }

  // ✏️ Post creation endpoint
  async createPost(body: string, visibility: string): Promise<CreatedPost> {
    return this.request<CreatedPost>("/api/social/posts", {
      method: "POST",
      body: JSON.stringify({ body, visibility }),
      auth: true,
    });
  }

  // 🗑️ Post deletion
  async deletePost(postId: string) {
    return this.request<{ message: string }>(`/api/social/posts/${postId}`, {
      method: "DELETE",
      auth: true,
    });
  }

  // ✏️ Post editing
  async editPost(postId: string, body: string) {
    return this.request<{ postId: string; body: string; updatedAt: string }>(`/api/social/posts/${postId}`, {
      method: "PATCH",
      body: JSON.stringify({ body }),
      auth: true,
    });
  }

  async getPost(postId: string): Promise<FeedItem> {
    return this.request<FeedItem>(`/api/social/posts/${postId}`, {
      method: "GET",
      auth: true,
    });
  }

  // 💬 Comment endpoints
  async getComments(postId: string, cursor?: string, limit: number = 10) {
    const params = new URLSearchParams();
    if (cursor) params.append("cursor", cursor);
    params.append("limit", limit.toString());
    const qs = params.toString();
    return this.request<CommentsResponse>(
      `/api/social/posts/${postId}/comments${qs ? `?${qs}` : ""}`,
      { method: "GET", auth: true }
    );
  }

  async createComment(postId: string, body: string, parentCommentId?: string) {
    return this.request<{ comment: Comment }>(
      `/api/social/posts/${postId}/comments`,
      {
        method: "POST",
        body: JSON.stringify({ body, parentCommentId }),
        auth: true,
      }
    );
  }

  async deleteComment(postId: string, commentId: string) {
    return this.request<{ message: string }>(
      `/api/social/posts/${postId}/comments/${commentId}`,
      { method: "DELETE", auth: true }
    );
  }

  // 👍 Post like endpoints
  async likePost(postId: string) {
    return this.request<{ liked: boolean }>(`/api/social/posts/${postId}/like`, {
      method: "POST",
      auth: true,
    });
  }

  async unlikePost(postId: string) {
    return this.request<{ unliked: boolean }>(`/api/social/posts/${postId}/like`, {
      method: "DELETE",
      auth: true,
    });
  }

  async getPostLikers(postId: string, cursor?: string) {
    const params = new URLSearchParams();
    if (cursor) params.append("cursor", cursor);
    const qs = params.toString();
    return this.request<LikersResponse>(
      `/api/social/posts/${postId}/likes${qs ? `?${qs}` : ""}`,
      { method: "GET", auth: true }
    );
  }

  // 👍 Comment like endpoints
  async likeComment(postId: string, commentId: string) {
    return this.request<{ liked: boolean }>(
      `/api/social/posts/${postId}/comments/${commentId}/like`,
      { method: "POST", auth: true }
    );
  }

  async unlikeComment(postId: string, commentId: string) {
    return this.request<{ unliked: boolean }>(
      `/api/social/posts/${postId}/comments/${commentId}/like`,
      { method: "DELETE", auth: true }
    );
  }
}
